import React, { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../../../contexts/AuthContext';
import { useStripeContext } from '../../../contexts/StripeContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import stripePaymentService from '../../../services/stripePaymentService';

const EscrowPaymentForm = ({ clientSecret, offer, listing, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentReady, setPaymentReady] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  useEffect(() => {
    if (elements) {
      const paymentElement = elements?.getElement(PaymentElement);
      if (paymentElement) {
        paymentElement?.on('ready', () => {
          setPaymentReady(true);
        });
      }
    }
  }, [elements]);

  const handleSubmit = async (event) => {
    event?.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setErrorMessage('Payment system not ready. Please try again.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const { error: stripeError, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/marketplace`,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        setErrorMessage(stripeError?.message || 'Payment failed. Please try again.');
        onError?.(stripeError);
      } else if (paymentIntent) {
        if (paymentIntent?.status === 'requires_capture' || paymentIntent?.status === 'succeeded') {
          // Confirm payment on backend
          const { error } = await stripePaymentService?.confirmEscrowPayment(paymentIntent?.id);
          
          if (error) {
            setErrorMessage('Payment processed but confirmation failed. Please contact support.');
          } else {
            // Show success animation
            setShowSuccessAnimation(true);
            setTimeout(() => {
              setShowSuccessAnimation(false);
              onSuccess?.();
            }, 3000);
          }
        } else {
          setErrorMessage('Payment processing incomplete. Please try again.');
        }
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!stripe || !elements || !clientSecret) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/4"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-10 bg-muted rounded"></div>
        </div>
        <p className="text-sm text-muted-foreground mt-4 text-center">
          Loading payment system...
        </p>
      </div>
    );
  }

  const totalAmount = parseFloat(offer?.offer_amount || 0);
  const platformFee = stripePaymentService?.calculatePlatformFee(totalAmount);
  const sellerPayout = stripePaymentService?.calculateSellerPayout(totalAmount);

  return (
    <div className="relative">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Payment Breakdown */}
        <div className="bg-muted/20 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Item Price</span>
            <span className="font-medium text-foreground">
              {stripePaymentService?.formatAmount(totalAmount)}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Platform Fee (5%)</span>
            <span className="font-medium text-foreground">
              {stripePaymentService?.formatAmount(platformFee)}
            </span>
          </div>
          <div className="pt-2 border-t border-border flex items-center justify-between">
            <span className="font-semibold text-foreground">Total</span>
            <span className="text-xl font-bold text-primary">
              {stripePaymentService?.formatAmount(totalAmount)}
            </span>
          </div>
        </div>

        {/* Escrow Protection Notice */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Shield" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-green-900 mb-1">Escrow Protection</h4>
              <p className="text-xs text-green-700">
                Your payment is held securely until both you and the seller confirm the transaction. 
                Funds are only released when both parties agree.
              </p>
            </div>
          </div>
        </div>

        {/* Payment Element */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-card-foreground">
            Payment Details
          </label>
          <PaymentElement 
            options={{
              fields: {
                billingDetails: 'auto'
              },
              layout: 'tabs'
            }}
          />
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <p className="text-destructive text-sm">{errorMessage}</p>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={!paymentReady || isProcessing || !stripe || !elements}
          loading={isProcessing}
          iconName={isProcessing ? "Loader2" : "Lock"}
          iconPosition="left"
          fullWidth
          size="lg"
        >
          {isProcessing ? 'Processing Payment...' : `Pay ${stripePaymentService?.formatAmount(totalAmount)}`}
        </Button>

        {/* Security Notice */}
        <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
          <Icon name="Shield" size={14} />
          <span>Secured by Stripe • Your payment information is encrypted</span>
        </div>
      </form>
      {/* Success Animation Overlay */}
      {showSuccessAnimation && (
        <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-lg flex items-center justify-center z-50">
          <div className="text-center space-y-6 p-8">
            <div className="relative">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center animate-pulse">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
                  <Icon name="Check" size={32} className="text-white" />
                </div>
              </div>
              <div className="absolute inset-0 w-20 h-20 mx-auto border-4 border-green-300 rounded-full animate-ping opacity-30"></div>
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-green-600">Payment Successful! 🎉</h3>
              <p className="text-green-700 font-medium">Funds are held in escrow</p>
              <p className="text-sm text-green-600">Transaction protected until both parties confirm</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EscrowPaymentModal = ({ offer, listing, onClose, onSuccess }) => {
  const { userProfile } = useAuth();
  const { stripePromise, stripeOptions } = useStripeContext();
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    createPaymentIntent();
  }, []);

  const createPaymentIntent = async () => {
    setLoading(true);
    setError(null);

    const offerData = {
      offer_id: offer?.id,
      listing_id: listing?.id,
      listing_title: listing?.title,
      seller_id: listing?.seller_id,
      offer_amount: offer?.offer_amount,
      currency: offer?.currency || 'USD'
    };

    const buyerInfo = {
      id: userProfile?.id,
      full_name: userProfile?.full_name,
      email: userProfile?.email,
      stripeCustomerId: userProfile?.stripe_customer_id
    };

    const { data, error: paymentError } = await stripePaymentService?.createEscrowPayment(
      offerData,
      buyerInfo
    );

    if (paymentError) {
      setError(paymentError);
    } else {
      setClientSecret(data?.clientSecret);
    }

    setLoading(false);
  };

  if (!clientSecret && !loading && !error) {
    return null;
  }

  const elementsOptions = {
    clientSecret: clientSecret,
    ...stripeOptions,
    defaultValues: {
      billingDetails: {
        name: userProfile?.full_name || '',
        email: userProfile?.email || ''
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="CreditCard" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-card-foreground">Secure Escrow Payment</h2>
              <p className="text-sm text-muted-foreground">Complete your purchase with buyer protection</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading && (
            <div className="text-center py-12">
              <Icon name="Loader2" size={48} className="mx-auto text-primary animate-spin mb-4" />
              <p className="text-muted-foreground">Preparing secure payment...</p>
            </div>
          )}

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
              <Icon name="AlertCircle" size={48} className="mx-auto text-destructive mb-4" />
              <h3 className="text-lg font-semibold text-destructive mb-2">Payment Setup Failed</h3>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button onClick={createPaymentIntent} iconName="RefreshCw" iconPosition="left">
                Try Again
              </Button>
            </div>
          )}

          {clientSecret && !loading && !error && (
            <Elements stripe={stripePromise} options={elementsOptions}>
              <EscrowPaymentForm
                clientSecret={clientSecret}
                offer={offer}
                listing={listing}
                onSuccess={onSuccess}
                onError={(err) => setError(err?.message || 'Payment failed')}
              />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
};

export default EscrowPaymentModal;
